import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { navigate } from '../lib/router.ts';
import { Card } from '../components/Card.tsx';
import { EditableText } from '../components/EditableText.tsx';
import { ServiceSearchBox } from '../components/ServiceSearchBox.tsx';
import { Icon } from '../lib/icons.tsx';
import { share } from '../lib/share.ts';
import type { ServiceCardDTO } from '@newflix/shared';

interface CollectionPageProps {
  code: string;
  favs: string[];
  onFav: (slug: string) => void;
  onOpen: (slug: string) => void;
}

const KIND_LABEL: Record<string, string> = {
  private: '비공개',
  public: '공개 컬렉션',
  sponsored: '스폰서',
};

export function CollectionPage({ code, favs, onFav, onOpen }: CollectionPageProps) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['collection', code],
    queryFn: () => api.getCollectionByCode(code),
  });
  const [busy, setBusy] = useState(false);

  const collectionId = q.data?.collection.id;
  const isOwner = q.data?.isOwner ?? false;

  const refresh = () => qc.invalidateQueries({ queryKey: ['collection', code] });

  if (q.isPending) {
    return (
      <main className="nf-page" style={{ paddingTop: 120, textAlign: 'center', color: 'var(--fg-muted)' }}>
        불러오는 중…
      </main>
    );
  }
  if (q.error || !q.data) {
    return (
      <main className="nf-page" style={{ padding: '160px 56px', textAlign: 'center', color: 'var(--fg-muted)' }}>
        컬렉션을 찾을 수 없거나 비공개입니다.
      </main>
    );
  }

  const { collection: c, owner, items } = q.data;
  const services: ServiceCardDTO[] = items.map((it) => it.service);
  const serviceIds = items.map((it) => it.save.serviceId);
  const onShare = () => {
    share({ title: c.title, text: c.description ?? undefined, url: `/col/${c.publicCode}` });
  };

  // sponsored 컬렉션: 카드 클릭 = relay 바이패스로 외부 직행 + (컬렉션×서비스) 귀속.
  const handleOpen =
    c.kind === 'sponsored'
      ? (slug: string) => {
          window.location.href = `/r/${encodeURIComponent(slug)}?c=${encodeURIComponent(c.publicCode)}`;
        }
      : onOpen;

  const patch = async (body: Parameters<typeof api.updateCollection>[1]) => {
    if (!collectionId) return;
    await api.updateCollection(collectionId, body);
    refresh();
  };

  const move = async (idx: number, dir: -1 | 1) => {
    const next = idx + dir;
    if (!collectionId || next < 0 || next >= serviceIds.length) return;
    const reordered = [...serviceIds];
    [reordered[idx], reordered[next]] = [reordered[next]!, reordered[idx]!];
    setBusy(true);
    try {
      await api.reorderCollection(collectionId, reordered);
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const removeService = async (serviceId: string) => {
    if (!collectionId) return;
    setBusy(true);
    try {
      await api.removeServiceFromCollection(collectionId, serviceId);
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const addService = async (slug: string) => {
    if (!collectionId) return;
    setBusy(true);
    try {
      await api.addServiceToCollection(collectionId, slug);
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const deleteCollection = async () => {
    if (!collectionId) return;
    if (!window.confirm(`"${c.title}" 컬렉션을 삭제할까요? (담긴 서비스는 찜에 남습니다)`)) return;
    await api.deleteCollection(collectionId);
    qc.invalidateQueries({ queryKey: ['my-collections'] });
    navigate('/favorites');
  };

  return (
    <>
      <section className="nf-detail-hero">
        <div className="nf-detail-hero-content">
          <div className="nf-detail-hero-left">
            <div className="nf-detail-eyebrow">
              <span className="b" />
              <span>{KIND_LABEL[c.kind] ?? '컬렉션'}</span>
              <span>·</span>
              <span>서비스 {services.length}개</span>
              {isOwner && (
                <>
                  <span>·</span>
                  <span style={{ color: 'var(--accent)' }}>내 컬렉션 (편집 가능)</span>
                </>
              )}
            </div>
            <h1 className="nf-detail-title">
              <EditableText
                enabled={isOwner}
                value={c.title}
                onSave={(v) => patch({ title: v })}
                placeholder="컬렉션 제목"
              />
            </h1>
            {c.description && <p className="nf-detail-tag">{c.description}</p>}
            <div className="nf-detail-meta">
              {owner && (
                <>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: 'var(--fg)' }}>
                    {owner.avatar ? (
                      <img src={owner.avatar} alt="" width={24} height={24} style={{ borderRadius: '50%', objectFit: 'cover' }} />
                    ) : null}
                    {owner.displayName}
                  </span>
                  <span style={{ color: 'var(--fg-dim)' }}>·</span>
                </>
              )}
              <span>큐레이션</span>
            </div>
            <div className="nf-detail-actions">
              <button className="nf-btn primary" onClick={onShare}>
                <Icon.share /> 링크 복사
              </button>
              {isOwner && (
                <select
                  className="nf-coll-kind-select"
                  value={c.kind}
                  onChange={(e) => patch({ kind: e.target.value as 'private' | 'public' | 'sponsored' })}
                >
                  <option value="private">비공개</option>
                  <option value="public">공개</option>
                  <option value="sponsored">스폰서</option>
                </select>
              )}
            </div>
          </div>
          <div className="nf-detail-hero-right">
            {c.coverImage ? (
              <img src={c.coverImage} alt={c.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div className="nf-coll-cover-grid">
                {services.slice(0, 4).map((s) => (
                  <div key={s.slug} className="nf-coll-cover-cell">
                    {s.faviconUrl ? (
                      <img src={s.faviconUrl} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8 }} />
                    ) : (
                      <div style={{ width: 40, height: 40, borderRadius: 8, background: `linear-gradient(135deg, ${s.brandColor} 0%, ${s.accentColor} 100%)` }} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="nf-detail-body">
        <div>
          <div className="nf-section">
            <h3 className="nf-section-title">컬렉션 소개</h3>
            <p className="nf-intro">
              <EditableText
                enabled={isOwner}
                value={c.description ?? ''}
                onSave={(v) => patch({ description: v })}
                multiline
                placeholder="컬렉션을 소개해보세요."
              />
            </p>
          </div>

          {(c.curatorNote || isOwner) && (
            <div className="nf-section">
              <h3 className="nf-section-title">큐레이터의 한마디</h3>
              <div className="nf-maker">
                <p className="nf-maker-t">
                  <EditableText
                    enabled={isOwner}
                    value={c.curatorNote ?? ''}
                    onSave={(v) => patch({ curatorNote: v })}
                    multiline
                    placeholder="이 컬렉션을 왜 모았는지 한마디 남겨보세요."
                  />
                </p>
              </div>
            </div>
          )}

          <div className="nf-section nf-coll-add-section">
            <h3 className="nf-section-title">서비스 {services.length}개</h3>
            {isOwner && (
              <div style={{ marginBottom: 18 }}>
                <ServiceSearchBox
                  placeholder="서비스 검색해서 추가…"
                  actionLabel="추가"
                  clearOnSelect
                  isDisabled={(s) => items.some((it) => it.service.slug === s.slug)}
                  onSelect={(s) => addService(s.slug)}
                />
              </div>
            )}
            {services.length === 0 ? (
              <div style={{ color: 'var(--fg-muted)', fontSize: 14 }}>아직 담긴 서비스가 없습니다.</div>
            ) : (
              <div className="nf-coll-grid">
                {items.map((it, idx) => (
                  <div key={it.save.serviceId} style={{ position: 'relative' }}>
                    <Card service={it.service} isFav={favs.includes(it.service.slug)} onFav={onFav} onOpen={handleOpen} />
                    {isOwner && (
                      <div className="nf-coll-edit-ctl">
                        <button className="nf-img-ctl" disabled={busy || idx === 0} onClick={() => move(idx, -1)} title="앞으로">↑</button>
                        <button className="nf-img-ctl" disabled={busy || idx === items.length - 1} onClick={() => move(idx, 1)} title="뒤로">↓</button>
                        <button className="nf-img-ctl danger" disabled={busy} onClick={() => removeService(it.save.serviceId)} title="제거">✕</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {isOwner && (
            <details className="nf-fold">
              <summary>
                <h3 className="nf-section-title">컬렉션 삭제</h3>
              </summary>
              <div className="nf-fold-body">
                <p className="nf-me-help">
                  컬렉션을 삭제하면 복구할 수 없습니다. 담긴 서비스는 찜 목록에 그대로 남습니다.
                </p>
                <button className="nf-btn danger" onClick={deleteCollection}>
                  컬렉션 삭제
                </button>
              </div>
            </details>
          )}
        </div>
      </div>
    </>
  );
}

